import { createFileRoute } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import { Search } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { DataTable } from '@/components/custom/datatable'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Typography } from '@/components/ui/typography'
import { AddContactModal } from '@/components/custom/contacts/add-contact-modal'
import { ContactActionsMenu } from '@/components/custom/contacts/contact-actions-menu'
import { useContactsSearch } from '@/hooks/use-contacts'
import { useIsMobile } from '@/hooks/use-mobile'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import type { ColumnDef } from '@/components/custom/datatable/types'
import type { components } from '@/types/api.generated'
import type { Filter } from '@/types/api'
import {
  CONTACT_TITLE_LABELS,
  type ContactTitle,
} from '@/lib/schemas/contact.schema'
import { createColumn } from '@/lib/datatable/create-column'
import { formatContactName } from '@/lib/contacts/format-contact-name'

type Contact = components['schemas']['ContactResponseDto']

export const Route = createFileRoute('/_authenticated/contacts')({
  component: ContactsPage,
})

// Helper to format job title enum to display text
const formatJobTitle = (title: string | undefined): string => {
  if (!title) return ''
  return CONTACT_TITLE_LABELS[title as ContactTitle] ?? title
}

function ContactActionsCell({ row }: { row: { original: Contact } }) {
  return (
    <div className="flex justify-end">
      <ContactActionsMenu
        contactId={row.original.id}
        contactName={formatContactName(row.original)}
        contactTitle={row.original.assignments?.[0]?.title}
        removeMode="delete-contact"
      />
    </div>
  )
}

function getColumns(): ColumnDef<Contact>[] {
  const baseColumns: ColumnDef<Contact>[] = [
    createColumn({
      id: 'name',
      header: 'Name',
      accessorFn: (row) => formatContactName(row),
      width: 200,
      isFilterable: true,
      isSortable: true,
    }),
    createColumn({
      id: 'company',
      header: 'Company',
      accessorFn: (row) => row.assignments?.[0]?.company?.displayName,
      width: 200,
    }),
    createColumn({
      id: 'jobTitle',
      header: 'Job Title',
      accessorFn: (row) => formatJobTitle(row.assignments?.[0]?.title),
    }),
    createColumn({
      id: 'email',
      header: 'Email',
      accessorFn: (row) => row.email,
      width: 250,
      isFilterable: true,
      isSortable: true,
    }),
    createColumn({
      id: 'phone',
      header: 'Phone',
      accessorFn: (row) => row.phone,
    }),
    createColumn({
      id: 'notes',
      header: 'Notes',
      accessorFn: (row) => row.notes,
      width: 250,
    }),
  ]

  return [
    ...baseColumns,
    createColumn({
      id: 'actions',
      header: '',
      accessorFn: () => null,
      width: 64,
      cell: ({ row }) => <ContactActionsCell row={row} />,
    }),
  ]
}

function ContactsPage() {
  const isMobile = useIsMobile()

  // Search state
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 300)

  // Modal state
  const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false)
  const columns = useMemo(() => getColumns(), [])

  // Build filters from search (search firstName, lastName, and email with OR logic)
  const filters = useMemo((): Filter[] => {
    if (!debouncedSearch.trim()) return []
    const searchValue = debouncedSearch.trim()
    return [
      {
        field: 'firstName',
        op: 'contains',
        value: searchValue,
        logicalOp: 'OR',
      },
      {
        field: 'lastName',
        op: 'contains',
        value: searchValue,
        logicalOp: 'OR',
      },
      {
        field: 'email',
        op: 'contains',
        value: searchValue,
        logicalOp: 'OR',
      },
    ]
  }, [debouncedSearch])

  // Fetch contacts
  const {
    data,
    isLoading,
    isError,
    isLoadingMore,
    hasMore,
    fetchMore,
    refetch,
  } = useContactsSearch({
    filters,
    sorts: [
      { field: 'firstName', direction: 'asc' },
      { field: 'lastName', direction: 'asc' },
    ],
  })

  return (
    <div className="flex h-full min-w-0 flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between pr-4 md:pr-8">
        {isMobile ? (
          <Typography variant="h1" className="sr-only">
            Contacts
          </Typography>
        ) : (
          <PageHeader title="Contacts" useSeparator={false} />
        )}
        <Button onClick={() => setIsAddContactModalOpen(true)}>
          + New Contact
        </Button>
      </div>

      {/* Search bar */}
      <div className="pr-4 md:pr-8">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search contacts..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Contacts DataTable */}
      <DataTable
        data={data}
        columns={columns}
        isLoading={isLoading}
        isError={isError}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        onLoadMore={fetchMore}
        onRefresh={refetch}
        searchTerm={debouncedSearch}
        className="flex-1"
      />

      {/* Add Contact Modal */}
      <AddContactModal
        open={isAddContactModalOpen}
        onOpenChange={setIsAddContactModalOpen}
      />
    </div>
  )
}
